import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { migrateStepConfigOverrides } from './migrateStepOverrides';
import type { EditorStep, RegistryEntry } from './types';

/** Onboarding version query with step fields aligned to the GraphQL schema (isEnabled / isRequired). */
export const GET_ONBOARDING_VERSION = gql`
  query GetOnboardingVersion($id: ID!) {
    onboardingVersion(id: $id) {
      id
      version
      name
      description
      isDefault
      totalStepCount
      requiredStepCount
      createdAt
      steps {
        id
        stepCode
        position
        isEnabled
        configOverrides
        isRequired
        requiredOverride
        description
        rationale
      }
    }
  }
`;

/** Shape of a version step as returned by the `onboardingVersion` GraphQL query. */
export interface VersionStepInput {
  stepCode: string;
  position: number;
  isRequired: boolean;
  isEnabled: boolean;
  requiredOverride?: boolean | null;
  configOverrides: unknown;
  description?: string;
  rationale?: string;
}

export type MapVersionStepsOptions = {
  /** When true, omit disabled steps (e.g. welcome preview on the version detail page). */
  enabledOnly?: boolean;
};

/** Build a code → registry-entry lookup map, memoized on the registry list. */
export function useRegistryByCode(registry: RegistryEntry[] | undefined): Map<string, RegistryEntry> {
  return useMemo(() => {
    const m = new Map<string, RegistryEntry>();
    for (const e of registry ?? []) {
      m.set(e.code, e);
    }
    return m;
  }, [registry]);
}

/**
 * Map raw version steps into editor steps, sorted by position, with config overrides
 * migrated to the current schema. Preserves disabled steps and requiredOverride so
 * copying a version does not drop admin configuration. The GraphQL resolver does not
 * guarantee ordering, so callers must not rely on input order.
 */
export function mapVersionStepsToEditorSteps(
  steps: VersionStepInput[] | undefined,
  options?: MapVersionStepsOptions
): EditorStep[] {
  if (!steps) return [];
  const sorted = [...steps].sort((a, b) => a.position - b.position);
  const filtered = options?.enabledOnly ? sorted.filter((s) => s.isEnabled) : sorted;
  return filtered.map((s) => ({
    id: s.stepCode,
    stepCode: s.stepCode,
    position: s.position,
    isEnabled: s.isEnabled,
    required: s.isRequired,
    requiredOverride: s.requiredOverride ?? null,
    configOverrides: migrateStepConfigOverrides(s.stepCode, (s.configOverrides as Record<string, unknown>) || {}),
    description: s.description || '',
    rationale: s.rationale || '',
  }));
}
